import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { shareInFlight } from '@/lib/sharedRequest'
import { useAuth } from '@/hooks/useAuth'
import { logger } from '@/lib/logger'
import { scopeForDomain } from '@/lib/scope'
import { showToast } from '@/hooks/useToast'
import type {
  Note,
  DisplayNote,
  DbNote,
  NoteType,
  NoteSource,
  NoteEntityLink,
  DbNoteEntityLink,
  NoteEntityType,
  NoteLinkType,
  CreateNoteInput,
  UpdateNoteInput,
  CreateEntityLinkInput,
} from '@/types/note'
import type { Task } from '@/types/task'

// Explicit column list for notes list queries. Excludes the heavy `embedding`
// vector(1536) column (server-side only, ~2MB across all notes) — `select('*')`
// serializes every vector to text and trips the Postgres statement timeout
// (error 57014: "canceling statement due to statement timeout").
const NOTE_COLUMNS =
  'id,user_id,title,content,type,source,topic_id,audio_url,external_id,external_url,vault_path,vault_domain,vault_frontmatter,vault_last_commit_sha,context,timeline_at,created_at,updated_at'

// ============================================================================
// Converters
// ============================================================================
export function mapDbNote(dbNote: DbNote): Note {
  return {
    id: dbNote.id,
    title: dbNote.title ?? undefined,
    content: dbNote.content,
    type: dbNote.type as NoteType,
    source: dbNote.source as NoteSource,
    topicId: dbNote.topic_id ?? undefined,
    audioUrl: dbNote.audio_url ?? undefined,
    externalId: dbNote.external_id ?? undefined,
    externalUrl: dbNote.external_url ?? undefined,
    vaultPath: dbNote.vault_path ?? undefined,
    vaultDomain: dbNote.vault_domain ?? undefined,
    vaultFrontmatter: dbNote.vault_frontmatter ?? undefined,
    context: dbNote.context as Note['context'] ?? undefined,
    readonly: dbNote.source === 'vault',
    timelineAt: dbNote.timeline_at ? new Date(dbNote.timeline_at) : undefined,
    createdAt: new Date(dbNote.created_at),
    updatedAt: new Date(dbNote.updated_at),
  }
}

/** A bulk delete, held open long enough for an undo. */
export interface DeletedNotes {
  rows: DbNote[]
  links: DbNoteEntityLink[]
}

function dbEntityLinkToEntityLink(dbLink: DbNoteEntityLink): NoteEntityLink {
  return {
    id: dbLink.id,
    noteId: dbLink.note_id,
    entityType: dbLink.entity_type as NoteEntityType,
    entityId: dbLink.entity_id,
    linkType: dbLink.link_type as NoteLinkType,
    createdAt: new Date(dbLink.created_at),
  }
}

function taskToDisplayNote(task: Task): DisplayNote {
  return {
    id: `task-${task.id}`,
    content: task.notes || '',
    type: 'task_note' as NoteType,
    source: 'task' as NoteSource,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    sourceTaskId: task.id,
    sourceTaskTitle: task.title,
    lineageTaskId: task.sourceId,
  }
}

/** One row per jotting, not one per copy.
 *
 *  Placing a month task into a week copies the task and its notes, so a single
 *  note arrived in the stream once for the original and once for every copy —
 *  ten identical rows of the same potluck note. Copies that still say exactly
 *  what their original says are the same note; a copy whose notes were edited
 *  has become its own and stays. */
function collapseLineage(taskNotes: DisplayNote[]): DisplayNote[] {
  const byTaskId = new Map(taskNotes.map((n) => [n.sourceTaskId, n]))
  return taskNotes.filter((n) => {
    if (!n.lineageTaskId) return true
    const original = byTaskId.get(n.lineageTaskId)
    if (!original) return true
    return original.content.trim() !== n.content.trim()
  })
}

// ============================================================================
// Hook
// ============================================================================
/** Waits this long before the deferred load (see `deferLoad`). */
export const NOTES_DEFER_MS = 2500

export function useNotes({ deferLoad = false }: {
  /** Start the load a moment after mount instead of at once. The shell's
   *  provider mounts on every page, but nothing on most pages shows a note,
   *  and the load is the heaviest of startup — every note's full content
   *  (6.5 MB for 1k notes, ~1.2s in Postgres; prod 2026-10-13) — so at once
   *  it competed with the queries the page is waiting on. */
  deferLoad?: boolean
} = {}) {
  const { user } = useAuth()
  const [notes, setNotes] = useState<Note[]>([])
  const [taskNotes, setTaskNotes] = useState<DisplayNote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch notes and task notes on mount and when user changes
  useEffect(() => {
    if (!user) {
      logger.debug('[useNotes] No user, setting empty notes and loading=false')
      setNotes([])
      setTaskNotes([])
      setLoading(false)
      return
    }

    async function fetchNotesAndTasks() {
      if (!user) return

      logger.debug('[useNotes] Fetching notes and task notes for user:', user.id)
      setLoading(true)
      setError(null)

      // Fetch Second Brain notes. Shared: a single route mounts this hook
      // eight times over, and notes carry their full content.
      //
      // Legacy vault mirrors are left in the database (prod 2026-10-13: 985 of
      // 1,017 rows, 6.47 MB of the 6.49 MB of content) and were dropped only
      // after downloading every one of them — a 10–20s response that sometimes
      // timed out with a 500 and held up the rest of the page. They are left
      // out in the query now. `source` is nullable, so a note with no source
      // stays in.
      const { data: notesData, error: notesError } = await shareInFlight(
        `notes:${user.id}`,
        async () => await supabase
          .from('notes')
          .select(NOTE_COLUMNS)
          .eq('user_id', user.id)
          .or('source.is.null,source.neq.vault')
          .order('created_at', { ascending: false }),
      )

      if (notesError) {
        console.error('[useNotes] Error fetching notes:', notesError)
        setError(notesError.message)
        setLoading(false)
        return
      }

      // Fetch tasks that have notes. Shared for the same reason as the notes
      // query above — this one was still firing eight times per load.
      const { data: tasksData, error: tasksError } = await shareInFlight(
        `notes:tasks:${user.id}`,
        async () => await supabase
          .from('tasks')
          .select('*')
          .eq('user_id', user.id)
          .not('notes', 'is', null)
          .neq('notes', '')
          .order('updated_at', { ascending: false }),
      )

      if (tasksError) {
        console.error('[useNotes] Error fetching task notes:', tasksError)
        // Don't fail completely, just log the error
      }

      // Vault notes live in Obsidian and are no longer mirrored into Symphony.
      // Drop any legacy Supabase rows tagged source==='vault' from the list.
      const supabaseNotes = (notesData as DbNote[])
        .map(mapDbNote)
        .filter(n => n.source !== 'vault')

      const taskNotesConverted = collapseLineage(
        (tasksData || []).map((task: any) =>
          taskToDisplayNote({
            id: task.id,
            title: task.title,
            notes: task.notes,
            sourceId: task.source_id ?? undefined,
            createdAt: new Date(task.created_at),
            updatedAt: new Date(task.updated_at),
          } as Task)
        )
      )

      logger.debug('[useNotes] Fetched', supabaseNotes.length, 'notes,', taskNotesConverted.length, 'task notes')
      setNotes(supabaseNotes)
      setTaskNotes(taskNotesConverted)
      setLoading(false)
    }

    if (!deferLoad) {
      fetchNotesAndTasks()
      return
    }
    const timer = setTimeout(() => { void fetchNotesAndTasks() }, NOTES_DEFER_MS)
    return () => clearTimeout(timer)
  }, [user, deferLoad])

  // ============================================================================
  // CRUD Operations
  // ============================================================================

  const addNote = useCallback(
    async (input: CreateNoteInput): Promise<Note | null> => {
      if (!user) return null

      // Optimistic update
      const tempId = crypto.randomUUID()
      const optimisticNote: Note = {
        id: tempId,
        title: input.title,
        content: input.content,
        type: input.type ?? 'quick_capture',
        source: input.source ?? 'manual',
        topicId: input.topicId,
        context: input.context,
        timelineAt: input.timelineAt,
        createdAt: new Date(),
        updatedAt: new Date(),
      }
      setNotes((prev) => [optimisticNote, ...prev])

      const { data, error: insertError } = await supabase
        .from('notes')
        .insert({
          user_id: user.id,
          title: input.title ?? null,
          content: input.content,
          type: input.type ?? 'quick_capture',
          source: input.source ?? 'manual',
          topic_id: input.topicId ?? null,
          context: input.context ?? null,
          // notes RLS shares on scope, not context, so a family note without
          // this stays private to its author despite looking shared. Notes
          // carry no assignee, so the domain alone decides.
          scope: scopeForDomain(input.context ?? null, [], null),
          timeline_at: input.timelineAt?.toISOString() ?? null,
        })
        .select()
        .single()

      if (insertError) {
        // Rollback on error
        setNotes((prev) => prev.filter((n) => n.id !== tempId))
        setError(insertError.message)
        showToast("Couldn't save the note. Please try again.", 'error')
        return null
      }

      // Replace optimistic note with real one
      const realNote = mapDbNote(data as DbNote)
      setNotes((prev) => prev.map((n) => (n.id === tempId ? realNote : n)))

      return realNote
    },
    [user]
  )

  const updateNote = useCallback(
    async (id: string, updates: UpdateNoteInput): Promise<boolean> => {
      const note = notes.find((n) => n.id === id)
      if (!note) return false

      // Optimistic update
      setNotes((prev) =>
        prev.map((n) =>
          n.id === id
            ? {
                ...n,
                ...updates,
                topicId: updates.topicId === null ? undefined : (updates.topicId ?? n.topicId),
                updatedAt: new Date(),
              }
            : n
        )
      )

      // Convert to DB format
      const dbUpdates: Record<string, unknown> = {}
      if (updates.title !== undefined) dbUpdates.title = updates.title ?? null
      if (updates.content !== undefined) dbUpdates.content = updates.content
      if (updates.type !== undefined) dbUpdates.type = updates.type
      if (updates.topicId !== undefined) dbUpdates.topic_id = updates.topicId ?? null

      const { error: updateError } = await supabase.from('notes').update(dbUpdates).eq('id', id)

      if (updateError) {
        // Rollback on error
        setNotes((prev) => prev.map((n) => (n.id === id ? note : n)))
        setError(updateError.message)
        showToast("Couldn't save your note changes.", 'error')
        return false
      }
      return true
    },
    [notes]
  )

  const deleteNote = useCallback(
    async (id: string): Promise<void> => {
      // Save for rollback
      const noteToDelete = notes.find((n) => n.id === id)
      if (!noteToDelete) return

      // Optimistic update
      setNotes((prev) => prev.filter((n) => n.id !== id))

      const { error: deleteError } = await supabase.from('notes').delete().eq('id', id)

      if (deleteError) {
        // Rollback on error
        setNotes((prev) => {
          const index = prev.findIndex((n) => n.createdAt < noteToDelete.createdAt)
          if (index === -1) return [...prev, noteToDelete]
          return [...prev.slice(0, index), noteToDelete, ...prev.slice(index)]
        })
        setError(deleteError.message)
        showToast("Couldn't delete the note.", 'error')
      }
    },
    [notes]
  )

  /** What a bulk delete took away, and everything needed to put it back.
   *  Deleting a note CASCADES its entity links (note_entity_links → ON DELETE
   *  CASCADE), so an undo that only re-inserts the note quietly loses what it
   *  was attached to. Both travel together. */
  const deleteNotes = useCallback(
    async (ids: string[]): Promise<DeletedNotes | null> => {
      if (ids.length === 0) return null

      // Read the exact rows first — an undo re-inserts them verbatim (same id,
      // same created_at), so a restored note comes back where it was rather
      // than at the top of the stream as a new scrap.
      const { data: rows } = await supabase
        .from('notes')
        .select(NOTE_COLUMNS)
        .in('id', ids)
      const { data: links } = await supabase
        .from('note_entity_links')
        .select('*')
        .in('note_id', ids)

      const doomed = notes.filter((n) => ids.includes(n.id))
      setNotes((prev) => prev.filter((n) => !ids.includes(n.id)))

      const { error: deleteError } = await supabase.from('notes').delete().in('id', ids)

      if (deleteError) {
        setNotes((prev) => [...prev, ...doomed])
        setError(deleteError.message)
        showToast("Couldn't delete those notes.", 'error')
        return null
      }

      return { rows: (rows ?? []) as DbNote[], links: (links ?? []) as DbNoteEntityLink[] }
    },
    [notes],
  )

  const restoreNotes = useCallback(async (deleted: DeletedNotes): Promise<void> => {
    if (deleted.rows.length === 0) return

    setNotes((prev) => [...prev, ...deleted.rows.map(mapDbNote)])

    const { error: insertError } = await supabase.from('notes').insert(deleted.rows)
    if (insertError) {
      // The rows never came back — take them out of the list again rather than
      // showing notes that are not there.
      const ids = deleted.rows.map((r) => r.id)
      setNotes((prev) => prev.filter((n) => !ids.includes(n.id)))
      setError(insertError.message)
      showToast("Couldn't restore the deleted notes.", 'error')
      return
    }

    if (deleted.links.length > 0) {
      await supabase.from('note_entity_links').insert(deleted.links)
    }
  }, [])

  const appendToNote = useCallback(async (id: string, block: string, anchor: Date | null) => {
    const existing = notes.find(n => n.id === id)
    if (!existing) return null
    const stamp = new Date().toISOString()
    const content = `${existing.content}\n\n— ${stamp} —\n${block}`
    const patch: Record<string, unknown> = { content }
    if (anchor) patch.timeline_at = anchor.toISOString()  // append-also-anchors
    const { error } = await supabase.from('notes').update(patch).eq('id', id)
    if (error) { setError(error.message); return null }
    setNotes(prev => prev.map(n => n.id === id
      ? { ...n, content, timelineAt: anchor ?? n.timelineAt } : n))
    return id
  }, [notes])

  // ============================================================================
  // Entity Links
  // ============================================================================

  const addEntityLink = useCallback(
    async (noteId: string, input: CreateEntityLinkInput): Promise<NoteEntityLink | null> => {
      const { data, error: insertError } = await supabase
        .from('note_entity_links')
        .insert({
          note_id: noteId,
          entity_type: input.entityType,
          entity_id: input.entityId,
          link_type: input.linkType ?? 'related',
        })
        .select()
        .single()

      if (insertError) {
        setError(insertError.message)
        return null
      }

      return dbEntityLinkToEntityLink(data as DbNoteEntityLink)
    },
    []
  )

  const removeEntityLink = useCallback(async (linkId: string): Promise<void> => {
    const { error: deleteError } = await supabase.from('note_entity_links').delete().eq('id', linkId)

    if (deleteError) {
      setError(deleteError.message)
    }
  }, [])

  const getEntityLinks = useCallback(async (noteId: string): Promise<NoteEntityLink[]> => {
    const { data, error: fetchError } = await supabase
      .from('note_entity_links')
      .select('*')
      .eq('note_id', noteId)

    if (fetchError) {
      setError(fetchError.message)
      return []
    }

    return (data as DbNoteEntityLink[]).map(dbEntityLinkToEntityLink)
  }, [])

  const getNotesForEntity = useCallback(
    async (entityType: NoteEntityType, entityId: string): Promise<Note[]> => {
      const { data: links, error: linksError } = await supabase
        .from('note_entity_links')
        .select('note_id')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)

      if (linksError) {
        setError(linksError.message)
        return []
      }

      if (!links || links.length === 0) return []

      const noteIds = links.map((l) => l.note_id)
      const { data: notesData, error: notesError } = await supabase
        .from('notes')
        .select(NOTE_COLUMNS)
        .in('id', noteIds)
        .order('created_at', { ascending: false })

      if (notesError) {
        setError(notesError.message)
        return []
      }

      return (notesData as DbNote[]).map(mapDbNote)
    },
    []
  )

  // ============================================================================
  // Query helpers
  // ============================================================================

  const getNoteById = useCallback(
    (id: string): Note | undefined => {
      return notes.find((n) => n.id === id)
    },
    [notes]
  )

  const searchNotes = useCallback(
    (query: string): Note[] => {
      if (!query.trim()) return notes
      const lowerQuery = query.toLowerCase()
      return notes.filter(
        (n) =>
          n.content.toLowerCase().includes(lowerQuery) ||
          (n.title && n.title.toLowerCase().includes(lowerQuery))
      )
    },
    [notes]
  )

  const getNotesByTopic = useCallback(
    (topicId: string | null): Note[] => {
      if (topicId === null) {
        return notes.filter((n) => !n.topicId)
      }
      return notes.filter((n) => n.topicId === topicId)
    },
    [notes]
  )

  const getNotesByType = useCallback(
    (type: NoteType): Note[] => {
      return notes.filter((n) => n.type === type)
    },
    [notes]
  )

  // Merge notes and task notes for display with deduplication for scratchpad notes
  const allNotes = useMemo<DisplayNote[]>(() => {
    let combined = [...notes, ...taskNotes]

    // Deduplicate scratchpad notes - keep only the most recent one per day
    const scratchpadsByDate = new Map<string, DisplayNote>()
    const nonScratchpadNotes: DisplayNote[] = []

    for (const note of combined) {
      const isScratchpad =
        'title' in note &&
        note.title === 'Scratch Pad' &&
        note.type === 'quick_capture'

      if (isScratchpad) {
        const dateKey = new Date(note.createdAt).toDateString()
        const existing = scratchpadsByDate.get(dateKey)

        // Keep the most recent scratchpad for each day
        if (!existing || note.updatedAt > existing.updatedAt) {
          scratchpadsByDate.set(dateKey, note)
        }
      } else {
        nonScratchpadNotes.push(note)
      }
    }

    // Combine deduplicated scratchpads with other notes
    combined = [...nonScratchpadNotes, ...Array.from(scratchpadsByDate.values())]

    return combined.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
  }, [notes, taskNotes])

  // Create a notes map for efficient lookup
  const notesMap = useMemo(() => {
    const map = new Map<string, Note | DisplayNote>()
    for (const note of allNotes) {
      map.set(note.id, note)
    }
    return map
  }, [allNotes])

  // Group notes by date for list view
  const notesByDate = useMemo(() => {
    const groups: { date: string; label: string; notes: DisplayNote[] }[] = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const weekAgo = new Date(today)
    weekAgo.setDate(weekAgo.getDate() - 7)

    const todayNotes: DisplayNote[] = []
    const yesterdayNotes: DisplayNote[] = []
    const thisWeekNotes: DisplayNote[] = []
    const olderNotes: DisplayNote[] = []

    for (const note of allNotes) {
      const noteDate = new Date(note.updatedAt)
      noteDate.setHours(0, 0, 0, 0)

      if (noteDate.getTime() === today.getTime()) {
        todayNotes.push(note)
      } else if (noteDate.getTime() === yesterday.getTime()) {
        yesterdayNotes.push(note)
      } else if (noteDate >= weekAgo) {
        thisWeekNotes.push(note)
      } else {
        olderNotes.push(note)
      }
    }

    if (todayNotes.length > 0) {
      groups.push({ date: 'today', label: 'Today', notes: todayNotes })
    }
    if (yesterdayNotes.length > 0) {
      groups.push({ date: 'yesterday', label: 'Yesterday', notes: yesterdayNotes })
    }
    if (thisWeekNotes.length > 0) {
      groups.push({ date: 'this-week', label: 'This Week', notes: thisWeekNotes })
    }
    if (olderNotes.length > 0) {
      groups.push({ date: 'older', label: 'Older', notes: olderNotes })
    }

    return groups
  }, [allNotes])

  return {
    notes: allNotes, // Return merged notes
    notesMap,
    notesByDate,
    loading,
    error,
    // CRUD
    addNote,
    updateNote,
    deleteNote,
    deleteNotes,
    restoreNotes,
    appendToNote,
    // Entity links
    addEntityLink,
    removeEntityLink,
    getEntityLinks,
    getNotesForEntity,
    // Queries
    getNoteById,
    searchNotes,
    getNotesByTopic,
    getNotesByType,
  }
}
